import { Table, Tooltip as ATooltip } from "antd";
import React from "react";
import { Bar, BarChart, LabelList, Tooltip, XAxis, YAxis } from "recharts";
import styled from "styled-components";
import { prettyCount } from "../../../utils/format";
import { BAR_COLORS, LANG_PROFICIENCY, MATCHEST_BY_MAPPER } from "../common/constant";
import GenderBar from "../common/gender-bar";
import { getMaxOfExperienceOrComps, getMetricsByGender } from "../common/util";
import { ReactComponent as MatchIcon } from '../../../assets/images/svgs/match_number.svg';

interface DataProps {
  formated_data: any,
  raw_data: any,
  type: string,
  identifier: string
}

type MatchesByAttributeProps = {
  data: DataProps
}

const GetTableColumns = (key: string, matches_analyze: any, cache_key: string): any => {
  if(key === 'matches_by_language'){
    return [
      {
        title: `Top 10 ${MATCHEST_BY_MAPPER[key]['column_title']}`,
        dataIndex: 'name',
        key: 'name',
        className: 'capitalize-text',
        render: (text: any, record: any) => (<>
          <ATooltip placement="topLeft" title={unescape(record.name)} arrowPointAtCenter>
            {unescape(record.name)}
          </ATooltip>
        </>)
      },
      ...LANG_PROFICIENCY.map((proficiency: any) => ({
        title: proficiency,
        dataIndex: proficiency,
        key: proficiency,
        align: 'center',
        className: 'capitalize-text',
        render: (text: any, record: any) => (<>
          <MatchIcon style={{marginRight: '2px'}}/>{`${prettyCount(record[proficiency] || 0)}`}
        </>)
      }))
    ]
  } else {
    return [
      {
        title: `Top 10 ${MATCHEST_BY_MAPPER[key]['column_title']}`,
        dataIndex: 'name',
        key: 'name',
        className: 'capitalize-text',
        render: (text: any, record: any) => (<>
          <ATooltip placement="topLeft" title={unescape(record.name)} arrowPointAtCenter>
            {unescape(record.name)}
          </ATooltip>
        </>)
      },
      {
        title: 'Matches',
        dataIndex: 'prfs',
        key: 'prfs',
        align: 'center',
        className: 'capitalize-text',
        render: (text: any, record: any) => (<>
          <MatchIcon style={{marginRight: '2px'}}/>{`${prettyCount(record.prfs.length)}`}
        </>)
      },
      {
        title: 'Women % | Men %',
        dataIndex: 'prfs',
        key: 'prfs',
        align: 'center',
        className: 'capitalize-text',
        render: (text: any, record: any) => {
          let _data: any = {};
          const _key = `${cache_key}_gender_${record.name}`;
          // @ts-ignore
          if (window._reports_cache[_key]) {
            // @ts-ignore
            _data = JSON.parse(window._reports_cache[_key]);
          } else {
            _data = getMetricsByGender(record.prfs, matches_analyze);
            // @ts-ignore
            window._reports_cache[_key] = JSON.stringify(_data);
          }
          if(record.prfs.length === 0) return (<>-</>);
          return(<>
            <span className="table-gender-bar">
              {Math.floor(((_data['cmetrics-female'] || 0) * 100) / record.prfs.length)}%
              <GenderBar hideLabels={true} width={40} height={18} data={_data} totalCount={record.prfs.length}></GenderBar>
              {Math.floor(((_data['cmetrics-male'] || 0) * 100) / record.prfs.length)}%
            </span>
          </>)
        }
      },
      {
        title: 'Gender Gap',
        dataIndex: 'prfs',
        key: 'prfs',
        align: 'center',
        className: 'capitalize-text',
        render: (text: any, record: any) => {
          let _data: any = {};
          const _key = `${cache_key}_gender_${record.name}`;
          // @ts-ignore
          if (window._reports_cache[_key]) {
            // @ts-ignore
            _data = JSON.parse(window._reports_cache[_key]);
          } else {
            _data = getMetricsByGender(record.prfs, matches_analyze);
            // @ts-ignore
            window._reports_cache[_key] = JSON.stringify(_data);
          }
          if(record.prfs.length === 0) return (<>-</>);
          const _val = Math.floor(((_data['cmetrics-female'] || 0) * 100) / record.prfs.length) - Math.floor(((_data['cmetrics-male'] || 0) * 100) / record.prfs.length);
          return (<>
            <span className="table-gender-bar">
              {_val > 0 ? `+${_val}` : _val}%
            </span>
          </>)
        }
      },
      {
        title: 'Top Experience',
        dataIndex: 'prfs',
        key: 'prfs',
        align: 'center',
        className: 'capitalize-text',
        render: (text: any, record: any) => {
          const _key = `${cache_key}_experience_map_${record.name}`;
          // @ts-ignore
          const _val: any = window._reports_cache[_key] ? window._reports_cache[_key] : getMaxOfExperienceOrComps(record.prfs, matches_analyze, 'experience_map') || '-';
          // @ts-ignore
          window._reports_cache[_key] = _val;
          return (<>
            <span className="table-gender-bar">
              {_val}
            </span>
          </>)
        }
      },
      {
        title: 'Top Company',
        dataIndex: 'prfs',
        key: 'prfs',
        align: 'left',
        className: 'capitalize-text',
        render: (text: any, record: any) => {
          const _key = `${cache_key}curr_comp_map_${record.name}`;
          // @ts-ignore
          const _val: any = window._reports_cache[_key] ? window._reports_cache[_key] : getMaxOfExperienceOrComps(record.prfs, matches_analyze, 'curr_comp_map') || '-';
          // @ts-ignore
          window._reports_cache[_key] = _val;
          return (<>
            <span className="table-gender-bar">
              {_val}
            </span>
          </>)
        }
      }
    ]
  }
};

const BarChartWrapper = styled.div`
  width: 845px;
  background-color: #F7F8FA;
  margin: 18px 0px;
  font-size: 10px;
  font-weight: 400;
  border-radius: 5px;
  text-transform: capitalize;
`;

const CustomizedAxisTick = (props:any) => {
  const { x, y, payload } = props
  const _val = payload.value.length > 20 ? `${payload.value.substring(0, 18)}...` : payload.value;
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={payload.index % 2 == 0 ? 6 : 20} x={_val.length > 10 ? 40 : 5} y={0} textAnchor="end" fill='#666'>
        {_val}
      </text>
    </g>
  )
}

const CustomizedLabel = (props:any) => {
  const { x, y, value } = props;
  return (
    <g>
      <text x={x} y={y} fill="#000" dy={-5} dx={3}>{prettyCount(value)}</text>
    </g>
  )
}

const MatchesByAttribute = (props: MatchesByAttributeProps) => {
  const { data  } = props;
  const { formated_data, raw_data, type, identifier } = data;
  const TransformData = (Obj: any, type: string):any => {
    let _data: any[] = [];
    Obj[type]?.forEach((obj:any) => {
      if(type === 'top_language'){
        _data.push({...obj, ...{ name: obj.name, count: obj.prfs?.length || 0}});
      }else{
        _data.push({ name: obj.name, count: obj.prfs?.length || 0});
      }
    });
    return _data;
  }
  return (<>
    {formated_data && raw_data && (<><BarChartWrapper style={{
      marginBottom: identifier === 'matches_by_language' ? '54px' : '18px'
    }}>
      <BarChart
        width={820}
        height={identifier === 'matches_by_language' ? 150 : 100}
        data={TransformData(formated_data, type)}
        margin={{
          top: 20,
          right: 20,
          left: 20,
          bottom: 5
        }}
        barSize={15}
      >
        <XAxis interval={0} dataKey="name" tick={<CustomizedAxisTick />} />
        <YAxis hide />
        <Tooltip labelClassName="capitalize-text"/>
        {identifier !== 'matches_by_language' && <Bar isAnimationActive={false} dataKey="count" fill="#75B2FF" minPointSize={5} radius={[2, 2, 0, 0]}>
          <LabelList dataKey="count" position="top" content={CustomizedLabel} className="capitalize-text"/>
        </Bar>}
        {identifier === 'matches_by_language' && LANG_PROFICIENCY.map((proficiency: any, index: number) => (<Bar key={index} isAnimationActive={false} dataKey={proficiency} stackId={'a'} fill={BAR_COLORS[index]}/>))}
      </BarChart>
    </BarChartWrapper>
    <div className="matches-table-container">
      <Table columns={GetTableColumns(identifier, raw_data, `${identifier}_${type}`)} dataSource={formated_data[type]} pagination={false} />
    </div></>)}
  </>)
}

export default MatchesByAttribute;
